import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const ORIGINAL_TITLE = "RodBez Operation";
const POLL_INTERVAL_MS = 3000;

/**
 * Conversations hook — polls /api/contacts and /api/messages every 3s.
 * Handles:
 *   • sound ding on new inbound
 *   • mobile vibration
 *   • service-worker based system notification (works on Android backgrounded)
 *   • tab title unread counter when the page is hidden
 */
export function useConversations({ soundOn = true } = {}) {
  const [contactsList, setContactsList] = useState([]);
  const [messagesByPhone, setMessagesByPhone] = useState({});
  const [source, setSource] = useState("polling");
  const [lastUpdate, setLastUpdate] = useState(null);
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const pollRef = useRef(null);
  const swRegRef = useRef(null);
  const unreadCountRef = useRef(0);

  const playDing = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(880, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);
      g.gain.setValueAtTime(0.18, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.35);
    } catch (_) {}
  };

  const updateTitleBadge = () => {
    if (unreadCountRef.current > 0 && document.hidden) {
      document.title = `(${unreadCountRef.current}) ${ORIGINAL_TITLE}`;
    } else {
      document.title = ORIGINAL_TITLE;
    }
  };

  const showSystemNotification = (msg) => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    const title = msg.name || "+" + msg.from;
    const body = (msg.text || "New message").slice(0, 160);
    const tag = `rodbez-${msg.from}`;
    if (swRegRef.current && swRegRef.current.active) {
      try {
        swRegRef.current.active.postMessage({ type: "NOTIFY", title, body, tag, url: "/" });
        return;
      } catch (_) {}
    }
    try {
      const n = new Notification(title, { body, tag, silent: true });
      n.onclick = () => {
        try { window.focus(); } catch (_) {}
        n.close();
      };
    } catch (_) {}
  };

  const notifyNew = (msg) => {
    if (!soundOn) return;
    if (!initializedRef.current) return;
    playDing();
    if (document.hidden) {
      unreadCountRef.current += 1;
      updateTitleBadge();
      try { if ("vibrate" in navigator) navigator.vibrate([180, 80, 180]); } catch (_) {}
      showSystemNotification(msg);
    }
  };

  // Register service worker + request notification permission + watch visibility
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/notify-sw.js")
        .then((reg) => { swRegRef.current = reg; })
        .catch(() => {});
    }
    if ("Notification" in window && Notification.permission === "default") {
      setTimeout(() => {
        try { Notification.requestPermission().catch(() => {}); } catch (_) {}
      }, 1500);
    }
    const onVis = () => {
      if (!document.hidden) {
        unreadCountRef.current = 0;
        updateTitleBadge();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    const onSwMsg = (e) => {
      if (e.data && e.data.type === "FOCUS_CHAT") {
        try { window.focus(); } catch (_) {}
      }
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onSwMsg);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", onSwMsg);
      }
      document.title = ORIGINAL_TITLE;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [msgsRes, contactsRes] = await Promise.all([
          api.get("/messages"),
          api.get("/contacts"),
        ]);
        if (cancelled) return;
        const byPhone = {};
        (msgsRes.data.messages || []).forEach((m) => {
          if (!byPhone[m.from]) byPhone[m.from] = [];
          byPhone[m.from].push(m);
          if (m.direction === "inbound" && !seenIdsRef.current.has(m.id)) {
            seenIdsRef.current.add(m.id);
            notifyNew(m);
          } else {
            seenIdsRef.current.add(m.id);
          }
        });
        Object.values(byPhone).forEach((arr) =>
          arr.sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""))
        );
        setMessagesByPhone(byPhone);
        setContactsList(contactsRes.data.contacts || []);
        setSource("polling");
        setLastUpdate(new Date().toISOString());
        setTimeout(() => { initializedRef.current = true; }, 500);
      } catch (_) {}
    };
    fetchAll();
    pollRef.current = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Merge: prefer contacts collection (has metadata), but include any phones with messages even if not in contacts
  const phonesInMessages = Object.keys(messagesByPhone);
  const contactsByPhone = Object.fromEntries(contactsList.map((c) => [c.phone, c]));
  for (const p of phonesInMessages) {
    if (!contactsByPhone[p]) {
      const last = messagesByPhone[p]?.slice(-1)[0] || {};
      contactsByPhone[p] = {
        phone: p,
        name: last.name || p,
        last_message: last.text || "",
        last_timestamp: last.timestamp || "",
        status: "new",
        unread: 0,
      };
    }
  }

  const contacts = Object.values(contactsByPhone)
    .map((c) => {
      const msgs = messagesByPhone[c.phone] || [];
      const last = msgs[msgs.length - 1] || {};
      return {
        ...c,
        last_direction: last.direction || null,
        last_message: c.last_message || last.text || "",
        last_timestamp: c.last_timestamp || last.timestamp || "",
      };
    })
    .sort((a, b) => (b.last_timestamp || "").localeCompare(a.last_timestamp || ""));

  return { contacts, messagesByPhone, source, lastUpdate };
}
